import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Terminal, Brain, Shield, Settings, Play, Database, Zap } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function BareMCPLanding() {
  const { t } = useTranslation('integrations');
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Create Bare MCP Integration
POST /api/v1/sockets/{socket_id}/integrations
{
  "integration_type": "bare_mcp",
  "integration_name": "My MCP Integration",
  "permissions": {
    "can_read": true,
    "can_write": true,
    "can_search": true,
    "can_delete": false
  }
}

# 2. Configure MCP Client (Claude Desktop)
Add to your MCP configuration:
{
  "mcpServers": {
    "memara": {
      "command": "uvx",
      "args": ["--from", "memara-mcp", "memara-mcp", "--api-key", "YOUR_API_KEY", "--socket-id", "YOUR_SOCKET_ID"],
      "env": {}
    }
  }
}

# 3. Available MCP Tools
- store_memory: Store new memories
- search_memories: Search existing memories
- list_memories: List recent memories
- get_memory: Get a memory by ID
- update_memory: Update a memory by ID
- delete_memory: Delete a memory by ID (requires Delete permission)
- get_socket_info: Get integration details

# 4. Test MCP Connection
Your MCP client will now have access to memory operations!`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=example', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="bare_mcp"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-50 shadow" aria-label="Any MCP client logo">
          <Terminal className="w-8 h-8 text-purple-600" />
        </span>
      }
      title="Any MCP client"
      description="Generic Model Context Protocol (MCP) server access for any MCP-compatible client. Connect Claude Desktop, VS Code, or custom MCP clients to your Memara memory system with standard MCP tools for memory operations."
      features={[
        { icon: <Terminal className="h-4 w-4 text-purple-600" />, title: 'Universal MCP Support', description: 'Works with any MCP-compatible client including Claude Desktop, VS Code, and custom applications.' },
        { icon: <Brain className="h-4 w-4 text-purple-600" />, title: 'Standard MCP Tools', description: 'Full MCP memory tools: store, search, list, get, update, delete, and get_socket_info — with permissions enforced per integration binding.' },
        { icon: <Shield className="h-4 w-4 text-purple-600" />, title: 'Secure Authentication', description: 'Bearer token authentication with configurable permissions and rate limiting for production use.' },
        { icon: <Database className="h-4 w-4 text-purple-600" />, title: 'Memory Space Isolation', description: 'Multi-tenant architecture with secure memory space isolation and per-integration scoping.' },
        { icon: <Zap className="h-4 w-4 text-purple-600" />, title: 'Real-time Memory Access', description: 'Instant memory operations with sub-second response times for seamless AI agent integration.' },
        { icon: <Settings className="h-4 w-4 text-purple-600" />, title: 'Flexible Configuration', description: 'Customizable permissions, rate limits, and content length limits to match your client needs.' },
      ]}
      setupSteps={[
        'Create an Any MCP client integration in your memory space',
        'Copy the wizard install command from the success screen',
        'Run the command in your project directory to install MCP and the memory skill',
        'Start using memory tools in Cursor, Claude Code, or your MCP client',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: t('wizard.cta.installWithWizard'),
          onClick: () => { window.location.href = '/wizard'; },
          className: 'bg-purple-50 text-purple-700 hover:bg-purple-100 px-8 py-4 h-11 rounded-md text-base border border-purple-200',
          icon: <Terminal className="w-4 h-4 mr-2" />,
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-purple-200 text-purple-600 hover:bg-purple-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-purple-200 text-purple-600 hover:bg-purple-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'mcp-integration-setup',
          title: 'Create MCP Integration',
          description: 'Set up your MCP client integration with configurable permissions and rate limits',
          placeholder: 'Integration setup modal showing MCP configuration options',
          badge: 'Step 1'
        },
        {
          id: 'mcp-client-config',
          title: 'Configure MCP Client',
          description: 'Add the MCP server configuration to your Claude Desktop or other MCP client',
          placeholder: 'MCP client configuration showing server setup and authentication',
          badge: 'Setup'
        },
        {
          id: 'mcp-tools-available',
          title: 'MCP Tools Available',
          description: 'Access memory operations through standard MCP tools in your client',
          placeholder: 'MCP client interface showing available memory tools and operations',
          badge: 'Ready'
        }
      ]}
    />
  );
}
